'use client';

import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatNumber } from '@/app/ops/helpers/formatters';
import { formatPercentual } from '@/app/relatorios/helpers/formatRelatorios';
import { CLASSES_DA_GRAVIDADE, GRAVIDADES } from '@/domain/ops/helpers/gravidadeDoFarol';
import { cn } from '@/lib/utils';

import { IRiscoDaVisao } from '../helpers/visaoExecutiva';
import { BarraSegmentada } from './BarraSegmentada';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';

interface RiscoBlocoProps {
  risco: IRiscoDaVisao;
  /** A Carteira já aberta no recorte do risco: a lista que está por trás do número. */
  href: string;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * O bloco de destaque: quanto a confecção pode deixar de faturar no prazo. O número é o
 * do cartão Risco financeiro da Carteira, pela mesma função (`resumoDoRisco`), e as duas
 * fatias da barra (já vencido e vai atrasar) fecham com ele.
 */
export function RiscoBloco({ risco, href, loading, failed, onRetry, className }: RiscoBlocoProps) {
  const { t } = useTranslation('common');

  const { resumo } = risco;
  const semRisco = resumo.ops === 0;
  /** O destaque é a cor do ASSUNTO: enquanto não há número lido, não há assunto. */
  const comRisco = !loading && !failed && !semRisco;

  /** Os graus que a carteira tem hoje, do pior para o melhor; grau zerado fica de fora. */
  const grausComOps = GRAVIDADES.map((grau) => ({ grau, n: risco.porGravidade[grau] })).filter((item) => item.n > 0);

  const fatias = [
    {
      chave: 'jaVencido',
      valor: resumo.jaVencido,
      classe: 'bg-danger',
      rotulo: t('visaoExecutiva.risco.jaVencido'),
    },
    {
      chave: 'vaiAtrasar',
      valor: resumo.vaiAtrasar,
      // O mesmo laranja do "no limite" do Mapa: o aviso é um só no produto inteiro.
      classe: 'bg-ocupacao-limite',
      rotulo: t('visaoExecutiva.risco.vaiAtrasar'),
    },
  ];

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.risco.pergunta')}
      // Sem a linha de origem: o número continua saindo da MESMA função do cartão da
      // Carteira (`resumoDoRisco`), e quem lê a tela não precisa da nota de rodapé.
      acao={comRisco ? { label: t('visaoExecutiva.risco.abrir'), href } : undefined}
      // Uma carteira sem risco nenhum num cartão vermelho leria como alarme onde a
      // notícia é boa.
      className={cn(comRisco && 'border-danger-border bg-danger-soft', className)}
    >
      <EstadoDoBloco loading={loading} failed={failed} onRetry={onRetry}>
        {semRisco ? (
          <NoDataFound variant="success" compact icon="verified" title={t('visaoExecutiva.risco.semRisco')} />
        ) : (
          <div className="flex animate-fade-in flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-3xl font-bold tabular-nums leading-none text-danger-emphasis">
                {formatCurrency(resumo.valor)}
              </span>
              <span className="text-xs text-foreground-muted">
                {t('visaoExecutiva.risco.pctFaturamento', { pct: formatPercentual(risco.pctFaturamento) })}
              </span>
              <span className="text-xs text-foreground-muted">
                {t('visaoExecutiva.risco.opsForaDoPrazo', {
                  ops: formatNumber(resumo.ops),
                  total: formatNumber(risco.opsCarteira),
                })}
              </span>
              {/* À parte do valor em risco: sem data não dá para afirmar atraso, mas
                  esconder a OP era pior, porque é a que ninguém consegue garantir. */}
              {resumo.opsSemPrevisao > 0 && (
                <span className="text-xs font-semibold text-warning-emphasis">
                  {t('visaoExecutiva.risco.semPrevisao', {
                    valor: formatCurrency(resumo.semPrevisao),
                    n: resumo.opsSemPrevisao,
                  })}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <BarraSegmentada
                total={resumo.valor}
                segmentos={fatias}
                size="md"
                rotulo={t('visaoExecutiva.risco.barra', {
                  vencido: formatCurrency(resumo.jaVencido),
                  atrasar: formatCurrency(resumo.vaiAtrasar),
                })}
              />
              <ul className="flex flex-wrap gap-x-4 gap-y-1">
                {fatias.map((fatia) => (
                  <li key={fatia.chave} className="flex items-center gap-1.5">
                    <span className={cn('size-2 shrink-0 rounded-sm', fatia.classe)} aria-hidden />
                    <span className="text-xs tabular-nums text-foreground-muted">
                      {t('visaoExecutiva.risco.fatia', {
                        rotulo: fatia.rotulo,
                        valor: formatCurrency(fatia.valor),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Por onde começar dentro do vermelho: a cor é a mesma do motor, o grau é o
                tamanho do atraso. Só aparecem os graus que existem hoje. */}
            {grausComOps.length > 0 && (
              <ul className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {grausComOps.map(({ grau, n }) => (
                  <li key={grau} className="flex items-center gap-1.5">
                    <span className={cn('size-2 shrink-0 rounded-sm', CLASSES_DA_GRAVIDADE[grau].fundo)} aria-hidden />
                    <span className="text-xs tabular-nums text-foreground-muted">
                      {t('visaoExecutiva.risco.grau', { rotulo: t(`ops.gravidade.${grau}`), n })}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* O mesmo corte do cartão da Carteira: não é uma terceira fatia, é o mesmo
                total lido de outro jeito (quanto do risco depende de um tempo estimado). */}
            <p className="text-xs text-foreground-subtle">
              {t('visaoExecutiva.risco.tempoEstimado', { valor: formatCurrency(resumo.comTempoEstimado) })}
            </p>
          </div>
        )}
      </EstadoDoBloco>
    </BlocoCard>
  );
}
